import urlUtil from 'url'

import { parse, stringify } from 'qs'
import { inHTMLData } from 'xss-filters'

import { TRACKING_DATA_KEYS } from 'shared/consts'
import { type TrackingData } from 'shared/utils/algolia/types'
import { pick } from 'shared/utils/objectUtils'

const safelyRetrieveParams = (param: unknown): string | undefined =>
  param ? inHTMLData(<string>param) : undefined

const parseObjectIDs = (objectIDs: string): string[] =>
  objectIDs?.replace(/[^a-zA-Z0-9,]/g, '').split(',')

/**
 * Checks if at least one tracking parameter is present
 * and tracking should be added to variants URLs
 */
const checkIfTrackingDataIsValid = (trackingData: TrackingData): boolean =>
  trackingData && Object.values(trackingData).some(Boolean)

/**
 * Retrieves algolia tracking data from the product URL
 */
export const getTrackingDataFromURL = (url: string): TrackingData => {
  const { query } = urlUtil.parse(url)
  const {
    eventName,
    eventType,
    objectIDs,
    position,
    query: searchQuery,
    queryID,
  } = parse(query || '')

  return {
    eventName: safelyRetrieveParams(eventName),
    eventType: safelyRetrieveParams(eventType),
    objectIDs: parseObjectIDs(
      Array.isArray(objectIDs) ? objectIDs.join(',') : <string>objectIDs
    ),
    position: safelyRetrieveParams(position),
    query: safelyRetrieveParams(searchQuery),
    queryID: safelyRetrieveParams(queryID),
  }
}

/**
 * Stringifies trackingData object into query params of the variant
 */
export const getTrackingDataUrlParams = (
  trackingData: TrackingData
): string => {
  if (checkIfTrackingDataIsValid(trackingData)) {
    const params = {
      ...pick(trackingData, TRACKING_DATA_KEYS),
      objectIDs: `[${trackingData.objectIDs?.toString() || ''}]`,
    }

    return `?` + stringify(params)
  }

  return ''
}

/**
 * Changes the objectIDs param in tracking data to corresponding variant ID
 */
export const getVariantTrackingData = (
  variantId: string,
  trackingData: TrackingData
) =>
  trackingData.objectIDs && variantId
    ? { ...trackingData, objectIDs: [variantId] }
    : trackingData
